import React from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';

import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { Button } from '@/components/ButtonComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { DevDemoStackParamList } from '@/feature/devsettings/DevDemoStack';

type InnerParams = {
    Level: { level: number };
};

type InnerNav = NativeStackNavigationProp<InnerParams>;
type OuterNav = NativeStackNavigationProp<DevDemoStackParamList>;

const InnerStack = createNativeStackNavigator<InnerParams>();

const LevelScreenContent = () => {
    const navigation = useNavigation<InnerNav>();
    const outerNavigation = useNavigation<InnerNav>().getParent<OuterNav>();
    const route = useRoute<RouteProp<InnerParams, 'Level'>>();
    const { level } = route.params;
    const insets = useSafeAreaInsets();

    return (
        <View style={[styles.root, { paddingTop: insets.top }]}>
            <ScreenNavBar title={`Level ${level}`} />
            <Box padding="s16" gap="s16">
                <Text variant="bodySmall" color="neutral300">
                    {level === 1
                        ? 'This modal has its own navigation stack. Pushing new screens stays inside this sheet — the sheet itself does not move. Tap "Push level 2" to try it.'
                        : `${level - 1} screen${
                              level > 2 ? 's' : ''
                          } deep inside this modal's stack. The sheet has not moved.`}
                </Text>
                <Button
                    label={`Push level ${level + 1}`}
                    onPress={() => navigation.push('Level', { level: level + 1 })}
                />
                {level > 1 ? (
                    <>
                        <Button label="Go back" onPress={() => navigation.goBack()} />
                        <Button label="Pop to root (level 1)" onPress={() => navigation.popToTop()} />
                    </>
                ) : null}
                <Button
                    label="Push fullscreen modal on top"
                    onPress={() => outerNavigation?.navigate('NavDemoFullScreen')}
                />
                <Button label="Dismiss modal" onPress={() => outerNavigation?.goBack()} />
            </Box>
        </View>
    );
};

const LevelScreen = () => (
    <SafeAreaProvider>
        <LevelScreenContent />
    </SafeAreaProvider>
);

export const ModalStackDemoScreen = () => (
    <InnerStack.Navigator initialRouteName="Level" screenOptions={{ headerShown: false }}>
        <InnerStack.Screen name="Level" component={LevelScreen} initialParams={{ level: 1 }} />
    </InnerStack.Navigator>
);

const styles = StyleSheet.create({
    root: {
        flex: 1,
        backgroundColor: ds.colorValue.background.neutral.base,
    },
});
